import { useEffect, useState } from "react";
import { Loader2, Save, Download, ClipboardEdit } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const NABH_MANUAL = [
    { key: "hand_hygiene", label: "Hand Hygiene Compliance (%)", unit: "%" },
    { key: "patient_falls", label: "Patient Falls (count)", unit: "count" },
    { key: "needle_stick", label: "Needle Stick Injuries (count)", unit: "count" },
    { key: "medication_errors", label: "Medication Errors (count)", unit: "count" },
    { key: "ssi", label: "Surgical Site Infections (count)", unit: "count" },
    { key: "cs_rate", label: "Caesarean Section Rate (%)", unit: "%" },
    { key: "bed_occupancy", label: "Bed Occupancy Rate (%)", unit: "%" },
    { key: "opd_waiting", label: "Average OPD Waiting Time (min)", unit: "min" },
    { key: "satisfaction", label: "Patient Satisfaction Score (/5)", unit: "/5" },
    { key: "complaints", label: "Patient Complaints (count)", unit: "count" },
    { key: "return_ot", label: "Return to OT within 48h (count)", unit: "count" },
    { key: "mortality", label: "Mortality (count)", unit: "count" },
];

export default function NabhManual() {
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [values, setValues] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/nabh/manual", { params: { month } });
                const map = {};
                data.forEach((e) => { map[e.key] = e.value; });
                setValues(map);
            } catch (e) { toast.error(fmtErr(e)); } finally { setLoading(false); }
        })();
    }, [month]);

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/nabh/manual", {
                month,
                entries: NABH_MANUAL.map((i) => ({ key: i.key, label: i.label, value: values[i.key] ?? "" })),
            });
            toast.success(`NABH data saved for ${month}`);
        } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(false); }
    };

    const exportCsv = () => {
        const lines = ["Indicator,Value,Month", ...NABH_MANUAL.map((i) => `"${i.label}","${values[i.key] ?? ""}","${month}"`)];
        const url = window.URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `nabh_manual_${month}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("CSV exported");
    };

    return (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="nabh-manual-card">
            <div className="px-5 py-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><ClipboardEdit className="h-4 w-4 text-[#E06D1F]" /> Manual Indicator Entry</h3>
                <div className="flex items-center gap-2">
                    <input type="month" data-testid="nabh-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-lg border border-slate-300 bg-white py-2 px-3 text-sm shadow-sm focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none" />
                    <Button data-testid="nabh-manual-save-btn" size="sm" onClick={save} disabled={saving} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save</Button>
                    <Button data-testid="nabh-manual-csv-btn" size="sm" variant="outline" onClick={exportCsv} className="gap-1.5"><Download className="h-3.5 w-3.5" /> CSV</Button>
                </div>
            </div>
            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
            ) : (
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {NABH_MANUAL.map((i) => (
                        <div key={i.key} className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                            <p className="text-xs font-medium text-slate-700">{i.label}</p>
                            <input
                                data-testid={`nabh-input-${i.key}`}
                                value={values[i.key] ?? ""}
                                onChange={(e) => setValues({ ...values, [i.key]: e.target.value })}
                                placeholder={i.unit}
                                className="mt-1.5 w-full bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-300 placeholder:font-normal border-b border-dashed border-slate-300 focus:border-[#E06D1F]"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
